import { findTracking } from "ts-tracking-number";
import { CanonicalCarrierCode } from "./carrier-registry";

const KNOWN_COURIER_CODES = [
  "amazon",
  "canada_post",
  "dhl",
  "dpd",
  "fedex",
  "landmark",
  "lasership",
  "old_dominion",
  "ontrac",
  "s10",
  "ups",
  "usps",
] as const;

type KnownCourierCode = (typeof KNOWN_COURIER_CODES)[number];

function parseKnownCourierCode(code: string): KnownCourierCode | undefined {
  if (KNOWN_COURIER_CODES.includes(code as KnownCourierCode)) {
    return code as KnownCourierCode;
  }
  return undefined;
}

type CourierResolver = (
  name: string,
  trackingCode: string,
) => CanonicalCarrierCode | undefined;

const fixed =
  (slug: CanonicalCarrierCode | undefined): CourierResolver =>
  () =>
    slug;

const S10_COUNTRY_TO_SLUG: Record<string, CanonicalCarrierCode> = {
  US: "usps",
  CA: "canada_post",
  GB: "royal_mail",
  AU: "australia_post",
  NZ: "nz_post",
  JP: "japan_post",
  CN: "china_post",
};

const COURIER_CODE_RESOLVERS: Record<KnownCourierCode, CourierResolver> = {
  amazon: fixed("amazon"),
  canada_post: fixed("canada_post"),
  dhl: (name) =>
    (
      ({
        "dhl express": "dhl_express",
        "dhl express (piece id)": "dhl_express",
        "dhl e-commerce": "dhl_ecommerce",
      }) as const
    )[name.toLowerCase()],
  dpd: fixed("dpd"),
  fedex: fixed("fedex"),
  landmark: fixed("landmark_global"),
  lasership: fixed("lasership"),
  old_dominion: fixed(undefined),
  ontrac: fixed("on_trac"),
  s10: (_name, trackingCode) =>
    S10_COUNTRY_TO_SLUG[trackingCode.slice(-2).toUpperCase()],
  ups: fixed("ups"),
  usps: fixed("usps"),
};

export function inferCarrierFromTrackingCodeThirdPartyLibrary(
  trackingCode: string,
): CanonicalCarrierCode | undefined {
  const [result] = findTracking(trackingCode);
  if (!result) {
    return undefined;
  }

  const courierCode = parseKnownCourierCode(result.courier.code);
  if (!courierCode) {
    return undefined;
  }

  return COURIER_CODE_RESOLVERS[courierCode](result.name, trackingCode);
}
